import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import AppShell from "../../components/layout/AppShell";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Handshake, Wallet, Coins, Users, Receipt, ArrowsClockwise,
  Copy, ToggleLeft, ToggleRight, Phone, Tag, ShieldCheck, ShareNetwork,
  WarningCircle, ArrowSquareOut, Printer, Percent,
} from "@phosphor-icons/react";

export default function PartnerProfile() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [payoutOpen, setPayoutOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { setData((await api.get(`/partners/${id}`)).data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const toggleCommission = async () => {
    if (!data) return;
    if (!window.confirm(`Turn commission ${data.partner.commission_enabled ? "OFF" : "ON"} for ${data.partner.name}?`)) return;
    setBusy(true);
    try {
      await api.put(`/partners/${id}`, { commission_enabled: !data.partner.commission_enabled });
      toast.success("Updated");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const updatePct = async (pct) => {
    setBusy(true);
    try {
      await api.put(`/partners/${id}`, { commission_pct: pct });
      toast.success(`Commission set to ${Math.round(pct*100)}%`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const updateBase = async (base) => {
    setBusy(true);
    try {
      await api.put(`/partners/${id}`, { commission_base: base });
      toast.success(`Base set to ${base.replace(/_/g," ")}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const copyLink = async (kind = "register") => {
    try {
      const r = await api.get(`/partners/${id}/link`);
      const raw = (kind === "profile" ? r.data.profile_url : r.data.register_url);
      const url = raw.startsWith("/") ? `https://rescigo.in${raw}` : raw;
      await navigator.clipboard.writeText(url);
      toast.success(`${kind === "profile" ? "Profile" : "Signup"} link copied`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!data) return <AppShell title="Partner"><div className="p-6 text-sm text-[#5C5A55]">Loading…</div></AppShell>;

  const { partner: p, wallet: w, customers, commissions, payouts, ledger } = data;
  const commOn = p.commission_enabled !== false;

  return (
    <AppShell
      title={p.name}
      subtitle={`${p.type} partner · ${p.phone}`}
      actions={
        <div className="flex gap-2 print:hidden">
          <button onClick={() => nav("/customers/referrals/partners")}
                  className="h-10 px-3 inline-flex items-center gap-2 border border-[#E5E2D9] rounded-md text-sm">
            <ArrowLeft size={14}/>Back
          </button>
          <button onClick={() => copyLink("register")} data-testid="pp-copy-signup"
                  className="h-10 px-3 inline-flex items-center gap-2 border border-[#E5E2D9] rounded-md text-sm hover:bg-[#F7F6F2]">
            <ShareNetwork size={14}/>Signup link
          </button>
          <button onClick={() => window.print()} data-testid="pp-print"
                  className="h-10 px-3 inline-flex items-center gap-2 border border-[#E5E2D9] rounded-md text-sm hover:bg-[#F7F6F2]">
            <Printer size={14}/>Print
          </button>
          <button onClick={() => setPayoutOpen(true)}
                  disabled={!data.payout_enabled}
                  data-testid="pp-payout-btn"
                  title={
                    !commOn ? "Commission disabled for this partner"
                      : (w.available < data.payout_min ? `Available must be ≥ ₹${data.payout_min}` : "")
                  }
                  className="h-10 px-4 inline-flex items-center gap-2 bg-[#2D5A27] hover:bg-[#3A7033] disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-md text-sm font-medium">
            <Coins size={14} weight="bold"/>Process Payout
          </button>
        </div>
      }
    >
      {/* TOP — Identity + Wallet */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-5">
        {/* Basic info */}
        <div className="bg-white border border-[#E5E2D9] rounded-md p-5" data-testid="pp-basic">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-[#D4E0D1] rounded-full flex items-center justify-center">
              <Handshake size={22} color="#2D5A27" weight="fill"/>
            </div>
            <div className="min-w-0">
              <div className="font-heading text-lg truncate">{p.name}</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A8780]">Basic Information</div>
            </div>
          </div>
          <Row icon={Tag} label="Partner Type">{p.type}</Row>
          <Row icon={Phone} label="Phone">{p.phone}</Row>
          <Row icon={ShieldCheck} label="Status">
            {p.active === false
              ? <span className="text-[#C25D38]">Inactive</span>
              : <span className="text-[#2D5A27]">Active</span>}
          </Row>
          <Row icon={commOn ? ToggleRight : ToggleLeft} label="Commission">
            <div className="flex items-center gap-2">
              <span className={`px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] rounded ${commOn ? "bg-[#D4E0D1] text-[#2D5A27]" : "bg-[#F4C7B9] text-[#7A2E18]"}`}>
                {commOn ? `ON · ${Math.round((p.commission_pct ?? 0.10) * 100)}%` : "OFF"}
              </span>
              <button onClick={toggleCommission} disabled={busy} data-testid="pp-toggle-commission"
                      className="text-[11px] text-[#2D5A27] hover:underline disabled:opacity-50 print:hidden">
                {commOn ? "Turn OFF" : "Turn ON"}
              </button>
            </div>
          </Row>
          {commOn && (
            <>
              <Row icon={Percent} label="Commission %">
                <div className="flex items-center gap-1.5 print:hidden">
                  {[0.02, 0.05, 0.10].map(pp => (
                    <button key={pp} onClick={() => updatePct(pp)} disabled={busy}
                            data-testid={`pp-pct-${Math.round(pp*100)}`}
                            className={`px-2 py-0.5 rounded text-[11px] border ${
                              (p.commission_pct ?? 0.10) === pp
                                ? "bg-[#2D5A27] text-white border-[#2D5A27]"
                                : "bg-white text-[#5C5A55] border-[#E5E2D9] hover:bg-[#F7F6F2]"
                            }`}>{Math.round(pp*100)}%</button>
                  ))}
                </div>
                <span className="hidden print:inline">{Math.round((p.commission_pct ?? 0.10) * 100)}%</span>
              </Row>
              <Row label="Applied on">
                <div className="flex flex-col gap-1 print:hidden">
                  {[
                    { v: "package_price", l: "Package price only" },
                    { v: "invoice_total", l: "Invoice total of package" },
                  ].map(opt => (
                    <button key={opt.v} onClick={() => updateBase(opt.v)} disabled={busy}
                            data-testid={`pp-base-${opt.v}`}
                            className={`text-left px-2 py-1 rounded text-[11px] border ${
                              (p.commission_base || "package_price") === opt.v
                                ? "bg-[#EFF3EE] text-[#2D5A27] border-[#2D5A27]"
                                : "bg-white text-[#5C5A55] border-[#E5E2D9] hover:bg-[#F7F6F2]"
                            }`}>
                      {(p.commission_base || "package_price") === opt.v ? "● " : "○ "}{opt.l}
                    </button>
                  ))}
                </div>
                <span className="hidden print:inline">
                  {(p.commission_base || "package_price") === "package_price" ? "Package price only" : "Invoice total of package"}
                </span>
              </Row>
            </>
          )}
          {p.notes && <Row label="Notes">{p.notes}</Row>}
          <div className="text-[10px] text-[#8A8780] mt-4 pt-3 border-t border-[#F0ECE3]">
            Joined {p.created_at ? new Date(p.created_at).toLocaleDateString() : "—"}
            {p.created_by_name ? ` · by ${p.created_by_name}` : ""}
          </div>
        </div>

        {/* Wallet */}
        <div className="lg:col-span-2 bg-white border border-[#E5E2D9] rounded-md p-5" data-testid="pp-wallet">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Wallet size={14} className="text-[#2D5A27]"/>
              <div className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55]">Commission Wallet</div>
            </div>
            {!commOn && (
              <div className="inline-flex items-center gap-1 text-[10px] text-[#7A2E18] bg-[#F4C7B9] px-2 py-0.5 rounded uppercase tracking-wider">
                <WarningCircle size={11} weight="fill"/> Commission disabled — credits paused
              </div>
            )}
          </div>
          <div className="grid grid-cols-4 gap-4">
            <Stat label="Total Earned" value={`₹${Number(w.total_earned||0).toFixed(2)}`} />
            <Stat label="Available Balance" value={`₹${Number(w.available||0).toFixed(2)}`} accent={data.payout_enabled}/>
            <Stat label="Total Paid" value={`₹${Number(w.total_paid||0).toFixed(2)}`} />
            <Stat label="Pending" value={`₹${Number(w.pending||0).toFixed(2)}`} />
          </div>
          <div className="mt-4 pt-4 border-t border-[#F0ECE3] grid grid-cols-3 gap-4 text-center">
            <Mini label="Customers" value={customers.length} icon={Users}/>
            <Mini label="Commissions" value={commissions.length} icon={Coins}/>
            <Mini label="Payouts" value={payouts.length} icon={Receipt}/>
          </div>
        </div>
      </div>

      {/* DATA TABLES */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Section title="Customer List" testid="pp-customers" count={customers.length} icon={Users}>
          {customers.length === 0 ? <Empty>No customers tagged via this partner yet.</Empty> :
            <table className="w-full text-sm">
              <thead className="bg-[#F7F6F2] text-[10px] uppercase tracking-[0.2em] text-[#5C5A55]">
                <tr>
                  <th className="text-left px-4 py-2">Customer</th>
                  <th className="text-left px-3 py-2">Code</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-right px-4 py-2">Joined</th>
                </tr>
              </thead>
              <tbody>
                {customers.map(c => (
                  <tr key={c.id} className="border-t border-[#F0ECE3]">
                    <td className="px-4 py-2">
                      <Link to={`/customers/${c.id}`} className="hover:underline inline-flex items-center gap-1">
                        {c.name}<ArrowSquareOut size={10}/>
                      </Link>
                    </td>
                    <td className="px-3 py-2 font-mono text-[11px] text-[#2D5A27]">{c.customer_code || "—"}</td>
                    <td className="px-3 py-2">
                      <span className={`text-[9px] px-2 py-0.5 rounded uppercase tracking-wider ${
                        c.status === "active" || c.status === "approved" ? "bg-[#D4E0D1] text-[#1A3317]" :
                        c.status === "pending_verification" ? "bg-[#F9E4B7] text-[#7A5A11]" :
                        "bg-[#F4C7B9] text-[#7A2E18]"
                      }`}>{(c.status || "—").replace(/_/g," ")}</span>
                    </td>
                    <td className="px-4 py-2 text-right text-[11px] text-[#8A8780]">
                      {c.created_at ? new Date(c.created_at).toLocaleDateString() : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          }
        </Section>

        <Section title="Commission History" testid="pp-commissions" count={commissions.length} icon={Coins}>
          {commissions.length === 0 ? <Empty>No commissions credited yet — partner must be commission-enabled and onboard customers who pay for a package.</Empty> :
            <table className="w-full text-sm">
              <thead className="bg-[#F7F6F2] text-[10px] uppercase tracking-[0.2em] text-[#5C5A55]">
                <tr>
                  <th className="text-left px-4 py-2">Customer</th>
                  <th className="text-left px-3 py-2">Invoice</th>
                  <th className="text-right px-3 py-2">Pkg Total</th>
                  <th className="text-right px-3 py-2">10%</th>
                  <th className="text-right px-4 py-2">Date</th>
                </tr>
              </thead>
              <tbody>
                {commissions.map(c => (
                  <tr key={c.id} className="border-t border-[#F0ECE3]">
                    <td className="px-4 py-2">
                      <div>{c.customer_name}</div>
                      <div className="text-[10px] font-mono text-[#8A8780]">{c.customer_code}</div>
                    </td>
                    <td className="px-3 py-2 font-mono text-[11px]">{c.invoice_no}</td>
                    <td className="px-3 py-2 text-right font-mono text-[#5C5A55]">₹{Number(c.invoice_total||0).toFixed(2)}</td>
                    <td className="px-3 py-2 text-right font-mono text-[#2D5A27] font-semibold">+₹{Number(c.commission_amount||0).toFixed(2)}</td>
                    <td className="px-4 py-2 text-right text-[11px] text-[#8A8780]">
                      {c.rewarded_at ? new Date(c.rewarded_at).toLocaleDateString() : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          }
        </Section>

        <Section title="Payment / Disbursement History" testid="pp-payouts" count={payouts.length} icon={Receipt}>
          {payouts.length === 0 ? <Empty>No payouts yet.</Empty> :
            <table className="w-full text-sm">
              <thead className="bg-[#F7F6F2] text-[10px] uppercase tracking-[0.2em] text-[#5C5A55]">
                <tr>
                  <th className="text-right px-4 py-2">Amount</th>
                  <th className="text-left px-3 py-2">Method</th>
                  <th className="text-left px-3 py-2">UPI Txn</th>
                  <th className="text-left px-3 py-2">Paid by</th>
                  <th className="text-right px-4 py-2">Date</th>
                </tr>
              </thead>
              <tbody>
                {payouts.map(po => (
                  <tr key={po.id} className="border-t border-[#F0ECE3]">
                    <td className="px-4 py-2 text-right font-mono text-[#7A2E18] font-semibold">−₹{Number(po.amount||0).toFixed(2)}</td>
                    <td className="px-3 py-2">
                      <span className="px-2 py-0.5 rounded text-[10px] uppercase tracking-[0.18em] bg-[#D4E0D1] text-[#2D5A27]">{po.method}</span>
                    </td>
                    <td className="px-3 py-2 font-mono text-[11px] text-[#5C5A55]">{po.upi_txn_id || "—"}</td>
                    <td className="px-3 py-2 text-[11px]">{po.paid_by_name || "—"}</td>
                    <td className="px-4 py-2 text-right text-[11px] text-[#8A8780]">{po.paid_date}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          }
        </Section>

        <Section title="Wallet Ledger" testid="pp-ledger" count={ledger.length} icon={ArrowsClockwise}>
          {ledger.length === 0 ? <Empty>No activity.</Empty> :
            <div>{ledger.slice(0, 20).map(l => (
              <div key={l.id} className="px-4 py-2 border-b border-[#F0ECE3] last:border-0 flex items-center gap-2 text-sm">
                <span className={`px-2 py-0.5 rounded text-[10px] uppercase tracking-[0.18em] ${l.type === "credit" ? "bg-[#D4E0D1] text-[#2D5A27]" : "bg-[#F4C7B9] text-[#7A2E18]"}`}>
                  {l.type}
                </span>
                <span className="text-xs text-[#5C5A55] flex-1 truncate">
                  {l.source}{l.invoice_no ? ` · ${l.invoice_no}` : ""}{l.method ? ` · ${l.method}` : ""}
                </span>
                <span className={`font-mono ${l.type === "credit" ? "text-[#2D5A27]" : "text-[#7A2E18]"}`}>
                  {l.type === "credit" ? "+" : "−"}₹{Number(l.amount||0).toFixed(2)}
                </span>
              </div>
            ))}</div>
          }
        </Section>
      </div>

      {payoutOpen && (
        <PayoutModal partner={p} max={w.available} payoutMin={data.payout_min}
                      onClose={() => setPayoutOpen(false)}
                      onDone={() => { setPayoutOpen(false); load(); }}/>
      )}
    </AppShell>
  );
}

const Row = ({ icon: I, label, children }) => (
  <div className="flex items-start gap-3 py-2 text-sm">
    {I && <I size={14} className="text-[#8A8780] mt-0.5"/>}
    <div className="flex-1 grid grid-cols-3 gap-2">
      <div className="text-[11px] uppercase tracking-[0.18em] text-[#8A8780]">{label}</div>
      <div className="col-span-2">{children}</div>
    </div>
  </div>
);

const Stat = ({ label, value, accent }) => (
  <div className={`rounded-md p-3 border ${accent ? "border-[#2D5A27] bg-[#EFF3EE]" : "border-[#E5E2D9] bg-[#F7F6F2]"}`}>
    <div className="text-[10px] uppercase tracking-[0.18em] text-[#5C5A55]">{label}</div>
    <div className={`text-xl font-light tabular-nums mt-1 ${accent ? "text-[#2D5A27]" : "text-[#1A1A1A]"}`}>{value}</div>
  </div>
);

const Mini = ({ label, value, icon: I }) => (
  <div>
    <div className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] text-[#8A8780]">
      {I && <I size={11}/>}{label}
    </div>
    <div className="text-base font-medium mt-0.5">{value}</div>
  </div>
);

const Section = ({ title, testid, count, icon: I, children }) => (
  <div className="bg-white border border-[#E5E2D9] rounded-md overflow-hidden" data-testid={testid}>
    <div className="px-5 py-3 border-b border-[#E5E2D9] flex items-center gap-2">
      {I && <I size={14}/>}
      <div className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] flex-1">{title}</div>
      <span className="text-xs text-[#8A8780]">{count}</span>
    </div>
    <div className="max-h-96 overflow-y-auto overflow-x-auto">{children}</div>
  </div>
);

const Empty = ({ children }) => (
  <div className="p-6 text-center text-xs text-[#8A8780]">{children}</div>
);

const PayoutModal = ({ partner, max, payoutMin, onClose, onDone }) => {
  const [f, setF] = useState({
    amount: "", method: "upi", upi_txn_id: "",
    paid_date: new Date().toISOString().slice(0, 10), notes: "",
  });
  const [busy, setBusy] = useState(false);
  const go = async () => {
    const amt = Number(f.amount);
    if (!amt || amt <= 0) { toast.error("Enter amount > 0"); return; }
    if (amt > max) { toast.error(`Max available is ₹${max.toFixed(2)}`); return; }
    if (max < payoutMin) { toast.error(`Available must be ≥ ₹${payoutMin}`); return; }
    if (f.method === "upi" && !f.upi_txn_id.trim()) { toast.error("UPI Txn ID required"); return; }
    setBusy(true);
    try {
      await api.post(`/partners/${partner.id}/payout`, { ...f, amount: amt });
      toast.success("Payout recorded");
      onDone();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-md max-w-md w-full p-6" onClick={e => e.stopPropagation()} data-testid="pp-payout-modal">
        <div className="font-heading text-lg mb-1">Process Payout</div>
        <div className="text-[11px] text-[#5C5A55] mb-4">{partner.name} · Available ₹{Number(max).toFixed(2)}</div>

        <L>Method *</L>
        <div className="grid grid-cols-2 gap-2 mb-3">
          {["upi", "cash"].map(m => (
            <button key={m} type="button" data-testid={`pm-${m}`}
                    onClick={() => setF(s => ({ ...s, method: m }))}
                    className={`h-10 rounded-md text-sm uppercase tracking-wider transition ${
                      f.method === m ? "bg-[#2D5A27] text-white" : "bg-[#F7F6F2] text-[#5C5A55] border border-[#E5E2D9]"
                    }`}>
              {m}
            </button>
          ))}
        </div>

        <L>Amount * (max ₹{Number(max).toFixed(2)})</L>
        <input type="number" step="0.01" value={f.amount}
               onChange={e => setF(s => ({ ...s, amount: e.target.value }))}
               data-testid="pm-amount"
               className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md text-sm mb-3 font-mono"/>

        {f.method === "upi" && (
          <>
            <L>UPI Transaction ID *</L>
            <input value={f.upi_txn_id} onChange={e => setF(s => ({ ...s, upi_txn_id: e.target.value }))}
                   data-testid="pm-utr"
                   className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md text-sm mb-3 font-mono"/>
          </>
        )}

        <L>Payment Date *</L>
        <input type="date" value={f.paid_date}
               onChange={e => setF(s => ({ ...s, paid_date: e.target.value }))}
               data-testid="pm-date"
               className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md text-sm mb-3"/>

        <L>Notes</L>
        <textarea value={f.notes} onChange={e => setF(s => ({ ...s, notes: e.target.value }))} rows={2}
                  className="w-full px-3 py-2 border border-[#E5E2D9] rounded-md text-xs mb-4"/>

        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="h-10 px-4 border border-[#E5E2D9] rounded-md text-sm">Cancel</button>
          <button onClick={go} disabled={busy} data-testid="pm-go"
                  className="h-10 px-4 bg-[#2D5A27] hover:bg-[#3A7033] disabled:opacity-60 text-white rounded-md text-sm font-medium">
            {busy ? "Processing…" : "Confirm Payout"}
          </button>
        </div>
      </div>
    </div>
  );
};

const L = ({ children }) => (
  <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-1.5">{children}</label>
);
